import { COUNTRY_MAP } from "./Countries";
import {
  US_STATE_MAP,
  CANADA_PROVINCE_MAP,
  US_STATES,
  CANADA_PROVINCES,
} from "./States";

const NULLISH_VALUES = new Set(["", "-", "--", "n/a", "na", "none", "null", "unknown"]);
const LOWERCASE_WORDS = new Set(["and", "de", "la", "of", "the"]);

const COUNTRY_NAME_BY_CODE = COUNTRY_MAP;
const COUNTRY_ALIAS_MAP = {
  US: "United States",
  USA: "United States",
  "U S": "United States",
  "U S A": "United States",
  "U.S": "United States",
  "U.S.": "United States",
  "U.S.A": "United States",
  "U.S.A.": "United States",
  "UNITED STATES": "United States",
  "UNITED STATES OF AMERICA": "United States",
  AMERICA: "United States",
  CA: "Canada",
  CAN: "Canada",
  CANADA: "Canada",
  GB: "United Kingdom",
  UK: "United Kingdom",
  "UNITED KINGDOM": "United Kingdom",
};

const STATE_ALIAS_MAP = {
  ...US_STATE_MAP,
  ...CANADA_PROVINCE_MAP,
  ...Object.fromEntries(
    [...US_STATES, ...CANADA_PROVINCES].map(({ name }) => [name.toUpperCase(), name]),
  ),
  "D.C.": "District of Columbia",
  "DISTRICT OF COLUMBIA": "District of Columbia",
  "WASHINGTON DC": "District of Columbia",
  "WASHINGTON D C": "District of Columbia",
  "WASHINGTON D.C.": "District of Columbia",
};

const sanitizeKey = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[.,]/g, "")
    .replace(/\s+/g, " ")
    .toUpperCase();

const cleanValue = (value) => {
  if (value == null) return "";
  const cleaned = String(value).trim().replace(/\s+/g, " ");
  if (!cleaned) return "";
  return NULLISH_VALUES.has(cleaned.toLowerCase()) ? "" : cleaned;
};

const titleCaseFragment = (fragment) => {
  if (!fragment) return fragment;
  return fragment.charAt(0).toUpperCase() + fragment.slice(1).toLowerCase();
};

const titleCaseWord = (word) => {
  if (!word) return word;

  for (const separator of ["-", "'"]) {
    if (word.includes(separator)) {
      return word.split(separator).map(titleCaseWord).join(separator);
    }
  }

  return titleCaseFragment(word);
};

export const normalizeString = (value) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return "";

  const words = cleaned.split(" ");
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index > 0 && index < words.length - 1 && LOWERCASE_WORDS.has(lower)) {
        return lower;
      }
      return titleCaseWord(word);
    })
    .join(" ");
};

export const normalizeCountry = (value) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return "";

  const key = sanitizeKey(cleaned);
  if (COUNTRY_ALIAS_MAP[key]) return COUNTRY_ALIAS_MAP[key];
  if (COUNTRY_NAME_BY_CODE[key]) return COUNTRY_NAME_BY_CODE[key];

  return normalizeString(cleaned);
};

export const normalizeState = (value) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return "";

  const key = sanitizeKey(cleaned);
  if (STATE_ALIAS_MAP[key]) return STATE_ALIAS_MAP[key];

  return normalizeString(cleaned);
};

export const normalizeCity = (value) => normalizeString(value);
export const normalizeIndustry = (value) => normalizeString(value);

const CONSULTANT_INDUSTRY_STANDARD_LABELS = {
  ADVANCED_MANUFACTURING: "Advanced Manufacturing",
  AEROSPACE_DEFENSE: "Aerospace & Defense",
  AGRICULTURE: "Agriculture",
  AUTOMOTIVE: "Automotive",
  BUSINESS_SERVICES: "Business Services",
  CALL_CENTERS: "Call Centers",
  CONSTRUCTION: "Construction",
  CORPORATE_SERVICES: "Business Services",
  DISTRIBUTION: "Distribution & Logistics",
  DISTRIBUTION_LOGISTICS: "Distribution & Logistics",
  EDUCATION: "Education",
  ENERGY: "Energy",
  FINANCIAL_SERVICES: "Financial Services",
  FOOD_BEVERAGE: "Food & Beverage",
  GOVERNMENT: "Government",
  HEALTHCARE: "Healthcare",
  HOSPITALITY: "Hospitality",
  LIFE_SCIENCES: "Life Sciences",
  LOGISTICS: "Logistics",
  MANUFACTURING: "Manufacturing",
  MEDIA_ENTERTAINMENT: "Media & Entertainment",
  METALS_MATERIALS: "Metals & Materials",
  OIL_GAS: "Oil & Gas",
  PACKAGING: "Packaging",
  NONPROFIT: "Nonprofit",
  PROFESSIONAL_SERVICES: "Professional Services",
  REAL_ESTATE: "Real Estate",
  RETAIL: "Retail",
  TECHNOLOGY: "Technology",
  TELECOMMUNICATIONS: "Telecommunications",
  TRANSPORTATION: "Transportation",
  UTILITIES: "Utilities",
  WHOLESALE: "Wholesale",
  CANNABIS: "Cannabis",
  SPORTS_RECREATION: "Sports & Recreation",
  SUSTAINABILITY: "Sustainability",
  OTHER: "Other",
};

const CONSULTANT_INDUSTRY_ALIASES = new Map(
  Object.entries({
    "AEROSPACE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AEROSPACE_DEFENSE,
    "AEROSPACE DEFENSE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AEROSPACE_DEFENSE,
    "AEROSPACE AND DEFENSE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AEROSPACE_DEFENSE,
    "AVIATION AEROSPACE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AEROSPACE_DEFENSE,
    "AVIATION AND AEROSPACE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AEROSPACE_DEFENSE,
    "DEFENSE SPACE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AEROSPACE_DEFENSE,
    "DEFENSE AND SPACE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AEROSPACE_DEFENSE,
    "AGRICULTURE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AGRICULTURE,
    "AGRICULTURE FORESTRY": CONSULTANT_INDUSTRY_STANDARD_LABELS.AGRICULTURE,
    "AUTOMOTIVE": CONSULTANT_INDUSTRY_STANDARD_LABELS.AUTOMOTIVE,
    "CONSTRUCTION": CONSULTANT_INDUSTRY_STANDARD_LABELS.CONSTRUCTION,
    "BUILDING MATERIALS": CONSULTANT_INDUSTRY_STANDARD_LABELS.CONSTRUCTION,
    "CONSTRUCTION BUILDING MATERIALS": CONSULTANT_INDUSTRY_STANDARD_LABELS.CONSTRUCTION,
    "CORPORATE": CONSULTANT_INDUSTRY_STANDARD_LABELS.CORPORATE_SERVICES,
    "CORPORATE SERVICES": CONSULTANT_INDUSTRY_STANDARD_LABELS.CORPORATE_SERVICES,
    "HOLDING COMPANIES": CONSULTANT_INDUSTRY_STANDARD_LABELS.CORPORATE_SERVICES,
    "MANAGEMENT OF COMPANIES AND ENTERPRISES":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.CORPORATE_SERVICES,
    "DISTRIBUTION": CONSULTANT_INDUSTRY_STANDARD_LABELS.DISTRIBUTION,
    "DISTRIBUTOR": CONSULTANT_INDUSTRY_STANDARD_LABELS.DISTRIBUTION,
    "EDUCATION": CONSULTANT_INDUSTRY_STANDARD_LABELS.EDUCATION,
    "EDUCATIONAL SERVICES": CONSULTANT_INDUSTRY_STANDARD_LABELS.EDUCATION,
    "SCHOOLS EDUCATION": CONSULTANT_INDUSTRY_STANDARD_LABELS.EDUCATION,
    "ENERGY": CONSULTANT_INDUSTRY_STANDARD_LABELS.ENERGY,
    "CLEAN ENERGY ENVIRONMENT": CONSULTANT_INDUSTRY_STANDARD_LABELS.ENERGY,
    "ENERGY ENVIRONMENTAL": CONSULTANT_INDUSTRY_STANDARD_LABELS.ENERGY,
    "FINANCE": CONSULTANT_INDUSTRY_STANDARD_LABELS.FINANCIAL_SERVICES,
    "BANKING": CONSULTANT_INDUSTRY_STANDARD_LABELS.FINANCIAL_SERVICES,
    "BANKS": CONSULTANT_INDUSTRY_STANDARD_LABELS.FINANCIAL_SERVICES,
    "FINANCE INSURANCE": CONSULTANT_INDUSTRY_STANDARD_LABELS.FINANCIAL_SERVICES,
    "FINANCIAL SERVICES": CONSULTANT_INDUSTRY_STANDARD_LABELS.FINANCIAL_SERVICES,
    "INSURANCE": CONSULTANT_INDUSTRY_STANDARD_LABELS.FINANCIAL_SERVICES,
    "FOOD": CONSULTANT_INDUSTRY_STANDARD_LABELS.FOOD_BEVERAGE,
    "BEVERAGE": CONSULTANT_INDUSTRY_STANDARD_LABELS.FOOD_BEVERAGE,
    "FOOD BEVERAGE": CONSULTANT_INDUSTRY_STANDARD_LABELS.FOOD_BEVERAGE,
    "FOOD AND BEVERAGE": CONSULTANT_INDUSTRY_STANDARD_LABELS.FOOD_BEVERAGE,
    "GOVERNMENT": CONSULTANT_INDUSTRY_STANDARD_LABELS.GOVERNMENT,
    "PUBLIC ADMINISTRATION": CONSULTANT_INDUSTRY_STANDARD_LABELS.GOVERNMENT,
    "HEALTH CARE": CONSULTANT_INDUSTRY_STANDARD_LABELS.HEALTHCARE,
    "HEALTHCARE": CONSULTANT_INDUSTRY_STANDARD_LABELS.HEALTHCARE,
    "HEALTH CARE SOCIAL ASSISTANCE": CONSULTANT_INDUSTRY_STANDARD_LABELS.HEALTHCARE,
    "HOSPITAL HEALTH CARE": CONSULTANT_INDUSTRY_STANDARD_LABELS.HEALTHCARE,
    "HOSPITAL AND HEALTH CARE": CONSULTANT_INDUSTRY_STANDARD_LABELS.HEALTHCARE,
    "HOSPITALS HEALTHCARE": CONSULTANT_INDUSTRY_STANDARD_LABELS.HEALTHCARE,
    "MEDICAL PRACTICE": CONSULTANT_INDUSTRY_STANDARD_LABELS.HEALTHCARE,
    "HOSPITALITY": CONSULTANT_INDUSTRY_STANDARD_LABELS.HOSPITALITY,
    "HOSPITALITY TOURISM": CONSULTANT_INDUSTRY_STANDARD_LABELS.HOSPITALITY,
    "TRAVEL TOURISM": CONSULTANT_INDUSTRY_STANDARD_LABELS.HOSPITALITY,
    "LEISURE SPORTS RECREATION": CONSULTANT_INDUSTRY_STANDARD_LABELS.HOSPITALITY,
    "LIFE SCIENCES": CONSULTANT_INDUSTRY_STANDARD_LABELS.LIFE_SCIENCES,
    "BIOTECHNOLOGY": CONSULTANT_INDUSTRY_STANDARD_LABELS.LIFE_SCIENCES,
    "PHARMACEUTICALS BIOTECHNOLOGY":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.LIFE_SCIENCES,
    "LOGISTICS": CONSULTANT_INDUSTRY_STANDARD_LABELS.LOGISTICS,
    "SUPPLY CHAIN": CONSULTANT_INDUSTRY_STANDARD_LABELS.LOGISTICS,
    "METAL MINERALS": "Metal & Minerals",
    "METALS MINERALS": "Metal & Minerals",
    "METAL AND MINERALS": "Metal & Minerals",
    "METALS AND MINERALS": "Metal & Minerals",
    "METAL MINING": "Metal & Minerals",
    "MINERALS": "Metal & Minerals",
    "MANUFACTURING": CONSULTANT_INDUSTRY_STANDARD_LABELS.MANUFACTURING,
    "INDUSTRIAL MANUFACTURING SERVICES":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.MANUFACTURING,
    "CHEMICALS": CONSULTANT_INDUSTRY_STANDARD_LABELS.MANUFACTURING,
    "PAPER": CONSULTANT_INDUSTRY_STANDARD_LABELS.MANUFACTURING,
    "WOOD": CONSULTANT_INDUSTRY_STANDARD_LABELS.MANUFACTURING,
    "NON PROFIT": CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "NONPROFIT": CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "NON PROFIT ORGANIZATION MANAGEMENT":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "GRANTMAKING GIVING SERVICES":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "CIVIC SOCIAL ORGANIZATIONS":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "RELIGIOUS ORGANIZATIONS": CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "SOCIAL ADVOCACY ORGANIZATIONS":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "VOLUNTARY HEALTH ORGANIZATIONS":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "COMMUNITY HOUSING SERVICES":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT,
    "PROFESSIONAL SERVICES": CONSULTANT_INDUSTRY_STANDARD_LABELS.PROFESSIONAL_SERVICES,
    "PROFESSIONAL SCIENTIFIC TECHNICAL SERVICES":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.PROFESSIONAL_SERVICES,
    "REAL ESTATE": CONSULTANT_INDUSTRY_STANDARD_LABELS.REAL_ESTATE,
    "REAL ESTATE RENTAL LEASING":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.REAL_ESTATE,
    "RETAIL": CONSULTANT_INDUSTRY_STANDARD_LABELS.RETAIL,
    "RETAIL TRADE": CONSULTANT_INDUSTRY_STANDARD_LABELS.RETAIL,
    "TECHNOLOGY": CONSULTANT_INDUSTRY_STANDARD_LABELS.TECHNOLOGY,
    "INFORMATION": CONSULTANT_INDUSTRY_STANDARD_LABELS.TECHNOLOGY,
    "COMPUTER HARDWARE": CONSULTANT_INDUSTRY_STANDARD_LABELS.TECHNOLOGY,
    "COMPUTER SOFTWARE": CONSULTANT_INDUSTRY_STANDARD_LABELS.TECHNOLOGY,
    "TELECOMMUNICATIONS": CONSULTANT_INDUSTRY_STANDARD_LABELS.TELECOMMUNICATIONS,
    "TRANSPORTATION": CONSULTANT_INDUSTRY_STANDARD_LABELS.TRANSPORTATION,
    "TRANSPORTATION WAREHOUSING":
      CONSULTANT_INDUSTRY_STANDARD_LABELS.TRANSPORTATION,
    "UTILITIES": CONSULTANT_INDUSTRY_STANDARD_LABELS.UTILITIES,
    "WHOLESALE": CONSULTANT_INDUSTRY_STANDARD_LABELS.WHOLESALE,
  }),
);

const CONSULTANT_INDUSTRY_KEYWORDS = [
  {
    label: CONSULTANT_INDUSTRY_STANDARD_LABELS.AEROSPACE_DEFENSE,
    keywords: ["AEROSPACE", "AVIATION", "DEFENSE", "MILITARY", "SPACE"],
  },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.AGRICULTURE, keywords: ["AGRICULTURE", "FARM", "FARMING", "FISHERY", "FORESTRY"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.AUTOMOTIVE, keywords: ["AUTOMOTIVE", "VEHICLE"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.CONSTRUCTION, keywords: ["ARCHITECTURE", "BUILDING", "CONSTRUCTION", "CONTRACTOR"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.CORPORATE_SERVICES, keywords: ["CORPORATE", "HEADQUARTERS", "HOLDING", "MANAGEMENT"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.DISTRIBUTION, keywords: ["DISTRIBUTION", "DISTRIBUTOR", "FULFILLMENT"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.EDUCATION, keywords: ["COLLEGE", "EDUCATION", "EDUCATIONAL", "SCHOOL", "TRAINING", "UNIVERSITY"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.ENERGY, keywords: ["CLEAN", "ENERGY", "ENVIRONMENTAL", "POWER", "RENEWABLE", "SOLAR", "WIND"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.FINANCIAL_SERVICES, keywords: ["BANK", "BANKING", "FINANCE", "FINANCIAL", "FINTECH", "INSURANCE"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.FOOD_BEVERAGE, keywords: ["BEVERAGE", "FOOD", "GROCERY", "RESTAURANT"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.GOVERNMENT, keywords: ["FEDERAL", "GOVERNMENT", "MUNICIPAL", "PUBLIC"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.HEALTHCARE, keywords: ["CARE", "HEALTH", "HEALTHCARE", "HOSPITAL", "MEDICAL", "PHYSICIAN", "WELLNESS"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.HOSPITALITY, keywords: ["ACCOMMODATION", "CASINO", "HOSPITALITY", "HOTEL", "LODGING", "TOURISM", "TRAVEL"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.LIFE_SCIENCES, keywords: ["BIOTECH", "BIOTECHNOLOGY", "LABORATORY", "PHARMA", "PHARMACEUTICAL"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.LOGISTICS, keywords: ["DELIVERY", "FREIGHT", "LOGISTICS", "SUPPLY", "WAREHOUSE", "WAREHOUSING"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.MANUFACTURING, keywords: ["CHEMICAL", "FABRICATION", "INDUSTRIAL", "MACHINERY", "MANUFACTURING", "PLANT", "PRINTING"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.NONPROFIT, keywords: ["CHARITY", "CHARITABLE", "COMMUNITY", "FOUNDATION", "NONPROFIT", "RELIGIOUS"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.PROFESSIONAL_SERVICES, keywords: ["ACCOUNTING", "CONSULTING", "ENGINEERING", "LEGAL", "PROFESSIONAL", "SCIENTIFIC", "TECHNICAL"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.REAL_ESTATE, keywords: ["PROPERTY", "REAL", "ESTATE"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.RETAIL, keywords: ["APPAREL", "CONSUMER", "ECOMMERCE", "RETAIL", "SHOPPING"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.TECHNOLOGY, keywords: ["COMPUTER", "CYBERSECURITY", "DATA", "ELECTRONICS", "INFORMATION", "INTERNET", "SOFTWARE", "TECHNOLOGY"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.TELECOMMUNICATIONS, keywords: ["TELECOM", "TELECOMMUNICATIONS", "WIRELESS", "WIRED"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.TRANSPORTATION, keywords: ["AIR", "TRANSPORT", "TRANSPORTATION", "TRUCKING"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.UTILITIES, keywords: ["UTILITIES", "UTILITY", "WATER"] },
  { label: CONSULTANT_INDUSTRY_STANDARD_LABELS.WHOLESALE, keywords: ["WHOLESALE"] },
];

const normalizeConsultantIndustryKey = (value) =>
  cleanValue(value)
    .toUpperCase()
    .replace(/&/g, " AND ")
    .replace(/[\\/,+()-]/g, " ")
    .replace(/\bAND\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const normalizeConsultantIndustry = (value) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return "";

  const key = normalizeConsultantIndustryKey(cleaned);
  if (!key) return "";

  const exactMatch = CONSULTANT_INDUSTRY_ALIASES.get(key);
  if (exactMatch) return exactMatch;

  const tokens = new Set(key.split(" ").filter(Boolean));

  let bestLabel = "";
  let bestScore = 0;

  for (const { label, keywords } of CONSULTANT_INDUSTRY_KEYWORDS) {
    const score = keywords.reduce(
      (count, keyword) => count + (tokens.has(keyword) ? 1 : 0),
      0,
    );
    if (score > bestScore) {
      bestLabel = label;
      bestScore = score;
    }
  }

  if (bestScore > 0) return bestLabel;
  return normalizeString(cleaned);
};

export const normalizeSubdivisionKey = (country, state) => {
  const normalizedCountry = normalizeCountry(country);
  const normalizedState = normalizeState(state);

  if (!normalizedCountry || !normalizedState) return "";
  return `${normalizedCountry}::${normalizedState}`;
};

export const buildNormalizedOptions = (values = [], normalizer = normalizeString) =>
  [...new Set(values.map((value) => normalizer(value)).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
